import { AuthApiError, AuthSdkError, OAuthError } from "@okta/okta-auth-js";
import * as Sentry from "@sentry/react";

import { genericErrorCopy } from "client/src/components/Error/ErrorMessage";

export type AuthFailure =
  | { kind: "invalid-passcode"; message: string }
  | { kind: "clock-skew"; message: string }
  | { kind: "general"; message: string };

const sessionExpired = {
  kind: "general",
  message: "Your verification session has expired. Start again to verify your identity." as const,
} satisfies AuthFailure;

const signInIncomplete = {
  kind: "general",
  message: "We could not finish signing you in. Please try again." as const,
} satisfies AuthFailure;

const clockSkew = {
  kind: "clock-skew",
  message:
    "Your computer clock is set more than 5 minutes from the Universal Time Clock. You’ll need to reset your computer clock to the proper time before you can continue in the system." as const,
} satisfies AuthFailure;

// Okta codes whose `errorSummary` is too cryptic to show as-is.
// https://developer.okta.com/docs/reference/error-codes/
const authApiErrorCodeFailures = new Map<string, AuthFailure>([
  [
    // "Authentication failed"
    "E0000004",
    {
      kind: "general",
      message: "You entered an incorrect email address, password, or both. Please try again.",
    },
  ],
  // "Invalid session"
  ["E0000005", sessionExpired],
  // "Invalid token provided" (expired state token)
  ["E0000011", sessionExpired],
  [
    // "API call exceeded rate limit due to too many requests."
    "E0000047",
    { kind: "general", message: "Too many attempts. Please wait a few minutes and try again." },
  ],
  [
    // "Factors Service Error."
    "E0000067",
    {
      kind: "general",
      message:
        "We can't verify your identity right now. Please try again or choose another verification method.",
    },
  ],
  [
    // "Invalid Passcode/Answer"
    "E0000068",
    { kind: "invalid-passcode", message: "That code isn't correct. Check the code and try again." },
  ],
  [
    // "User Locked"
    "E0000069",
    {
      kind: "general",
      message: "Too many unsuccessful attempts. Request a new verification code to continue.",
    },
  ],
  // "This operation is not allowed in the current authentication state."
  ["E0000079", sessionExpired],
  [
    // "PassCode is valid but exceeded time window."
    "E0000083",
    { kind: "invalid-passcode", message: "That code has expired. Request a new code to continue." },
  ],
]);

// AuthSdkError messages from token validation and the hidden `getWithoutPrompt` iframe.
const authSdkErrorFailures = new Map<string, AuthFailure>([
  ["The JWT was issued in the future", clockSkew],
  ["The JWT expired and is no longer valid", clockSkew],
  ["The JWT expired before it was issued", clockSkew],
  ["OAuth flow timed out", signInIncomplete],
  [
    "Could not load PKCE codeVerifier from storage. This may indicate the auth flow has already completed or multiple auth flows are executing concurrently.",
    signInIncomplete,
  ],
  ["OAuth flow response state doesn't match request state", signInIncomplete],
  ["OAuth flow response nonce doesn't match request nonce", signInIncomplete],
]);

export function authApiErrorToAuthFailure(error: unknown): AuthFailure {
  if (error instanceof AuthApiError) {
    const mapped = error.errorCode && authApiErrorCodeFailures.get(error.errorCode);
    if (mapped) return mapped;

    // "Api validation failed: factorEnrollRequest" keeps the useful detail in errorCauses,
    // for example "Invalid Phone Number."
    const cause = error.errorCauses?.[0]?.errorSummary;
    if (error.errorCode === "E0000001" && cause) return { kind: "general", message: cause };

    // The SDK wraps a failed fetch without a response, so the summary is browser-specific text
    // such as "Failed to fetch".
    if (!error.errorCode && !error.xhr) {
      return {
        kind: "general",
        message: "We could not connect. Check your internet connection and try again.",
      };
    }

    // The SDK replaces the summary of every 5xx response with "Unknown error".
    if (error.errorSummary === "Unknown error") {
      return { kind: "general", message: genericErrorCopy };
    }
  }

  if (error instanceof AuthSdkError) {
    const mapped = authSdkErrorFailures.get(error.errorSummary);
    if (mapped) return mapped;
  }

  // "The client specified not to prompt, but the user is not logged in."
  if (error instanceof OAuthError && error.errorCode === "login_required") {
    Sentry.captureMessage(error.errorSummary);
    return signInIncomplete;
  }

  if (
    error instanceof AuthApiError ||
    error instanceof AuthSdkError ||
    error instanceof OAuthError
  ) {
    const summary = error.errorSummary;

    if (!summary) return { kind: "general", message: genericErrorCopy };

    if (
      location.hostname === "localhost" &&
      /The issuer \[.*\] does not match \[.*\]/.test(summary)
    ) {
      return {
        kind: "general",
        message: summary + " - Try clearing your local storage: localStorage.clear()",
      };
    }

    return { kind: "general", message: summary };
  }

  const message = typeof error === "string" ? error : genericErrorCopy;

  return { kind: "general", message };
}
